import {
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  Pagination,
  Skeleton,
} from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate } from '../_helpers'
import { getErrorMessage } from '../../../../util/errorHandling'
import { getSeverityCount } from '../../../../util/severityCounts'
import {
  ListPaginationSkeleton,
  DASHBOARD_ROW_MIN_HEIGHT,
  DASHBOARD_ROW_PADDING_Y,
  DASHBOARD_CHIP_SLOT_HEIGHT,
} from '../../dashboardListUi'
import type { SeverityCount } from '@counsel-project/counsel-audit-ai-api'

type DocumentsListProps = {
  data?: {
    results?: Array<{
      documentId: string
      documentTitle: string
      documentAt: string
      patientName: string
      clinicianName: string
      alerts?: SeverityCount[]
    }>
    pagination?: {
      pages?: number
      total?: number
    }
  }
  isLoading: boolean
  error?: unknown
  currentPage: number
  setCurrentPage: (page: number) => void
}

const CHIP_SLOT_WIDTH = 104
const DOCUMENT_TITLE_SKELETON_HEIGHT = 20

export const DocumentsList = ({
  data,
  isLoading,
  error,
  currentPage,
  setCurrentPage,
}: DocumentsListProps) => {
  const navigate = useNavigate()
  const { results: documents = [], pagination } = data ?? {}

  const handleDocumentClick = (id: string) => {
    navigate(`/alerts?auditId=${id}`)
  }

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page)
  }

  const totalPages = pagination?.pages ?? 0
  const totalDocuments = pagination?.total ?? documents.length

  const renderLoadingContent = () => {
    const placeholderRows = ['doc-1', 'doc-2', 'doc-3', 'doc-4', 'doc-5']

    return (
      <>
        <List>
          {placeholderRows.map((rowId) => (
            <React.Fragment key={`document-loading-${rowId}`}>
              <ListItem
                alignItems="flex-start"
                sx={{ minHeight: DASHBOARD_ROW_MIN_HEIGHT, py: DASHBOARD_ROW_PADDING_Y }}
              >
                <ListItemText
                  sx={{ my: 0 }}
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={DOCUMENT_TITLE_SKELETON_HEIGHT}
                      />
                      <Skeleton variant="text" width={100} height={20} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Skeleton variant="rounded" width="40%" height={DASHBOARD_CHIP_SLOT_HEIGHT} />
                    </Box>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
        <ListPaginationSkeleton qaId="documents-list-pagination-loading" />
      </>
    )
  }

  const renderErrorContent = () => (
    <Typography variant="body2" color="text.secondary">
      {getErrorMessage(error, 'Failed to load documents')}
    </Typography>
  )

  const renderEmptyContent = () => (
    <Typography variant="body2" color="text.secondary">
      No documents found in the selected date range.
    </Typography>
  )

  const renderDocumentsContent = () => (
    <>
      <List>
        {documents.map((doc) => (
          <React.Fragment key={doc.documentId}>
            <ListItem
              alignItems="flex-start"
              button
              onClick={() => handleDocumentClick(doc.documentId)}
              sx={{
                cursor: 'pointer',
                minHeight: DASHBOARD_ROW_MIN_HEIGHT,
                py: DASHBOARD_ROW_PADDING_Y,
              }}
              data-qa-id={`documents-list-document-${doc.documentId}`}
            >
              <ListItemText
                sx={{ my: 0 }}
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1">{doc.documentTitle}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(doc.documentAt)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: DASHBOARD_CHIP_SLOT_HEIGHT,
                    }}
                  >
                    <Typography component="span" variant="body2" color="text.primary">
                      Patient: {doc.patientName}
                    </Typography>
                    <Box
                      sx={{
                        ml: 1,
                        width: CHIP_SLOT_WIDTH,
                        minHeight: DASHBOARD_CHIP_SLOT_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {getSeverityCount(doc, '3') > 0 && (
                        <Chip label="High Severity" size="small" color="error" />
                      )}
                    </Box>
                  </Box>
                }
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            data-qa-id="documents-list-pagination"
          />
        </Box>
      )}
    </>
  )

  const renderContent = () => {
    if (isLoading) return renderLoadingContent()
    if (error) return renderErrorContent()
    if (documents.length === 0) return renderEmptyContent()
    return renderDocumentsContent()
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Recent Documents{' '}
        {isLoading ? (
          <Skeleton variant="text" width={28} sx={{ display: 'inline-block' }} />
        ) : (
          `(${totalDocuments})`
        )}
      </Typography>
      {renderContent()}
    </Box>
  )
}
